import { useEffect, useState } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import {
  createIdentityAPIEndpoint,
  EndPoints,
} from "../../../services/OverViewAPIs";
import { Divider } from "antd";

function Overview() {
  const [totalStudents, setTotalStudents] = useState(null);
  const [activeStudents, setActiveStudents] = useState(null);
  const [inactiveStudents, setInactiveStudents] = useState(null);
  const [totalLessons, setTotalLessons] = useState(null);
  const [activeLessons, setActiveLessons] = useState(null);
  const [inactiveLessons, setInactiveLessons] = useState(null);
  const [totalCodes, setTotalCodes] = useState(null);
  const [activeCodes, setActiveCodes] = useState(null);
  const [inactiveCodes, setInactiveCodes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOverviewData = async () => {
      try {
        setLoading(true);
        await createIdentityAPIEndpoint(EndPoints.getTotalStudents)
          .fetch()
          .then((res) => setTotalStudents(res.data.totalStudents || 0))
          .catch((error) => {
            console.error("Failed to fetch total students", error);
          });

        await createIdentityAPIEndpoint(EndPoints.getTotalActiveStudents)
          .fetch()
          .then((res) => setActiveStudents(res.data.totalActiveStudents || 0))
          .catch((error) => {
            console.error("Failed to fetch active students", error);
          });

        await createIdentityAPIEndpoint(EndPoints.getTotalInactiveStudents)
          .fetch()
          .then((res) =>
            setInactiveStudents(res.data.totalInactiveStudents || 0)
          )
          .catch((error) => {
            console.error("Failed to fetch inactive students", error);
          });
        // Fetch lessons data similarly if needed
        await createIdentityAPIEndpoint(EndPoints.getTotalLessons)
          .fetch()
          .then((res) => setTotalLessons(res.data.totalLessons || 0))
          .catch((error) => {
            console.error("Failed to fetch total lessons", error);
          });
        await createIdentityAPIEndpoint(EndPoints.getTotalActiveLessons)
          .fetch()
          .then((res) => setActiveLessons(res.data.activeLessons || 0))
          .catch((error) => {
            console.error("Failed to fetch active lessons", error);
          });
        await createIdentityAPIEndpoint(EndPoints.getTotalInactiveLessons)
          .fetch()
          .then((res) => setInactiveLessons(res.data.inactiveLessons || 0))
          .catch((error) => {
            console.error("Failed to fetch inactive lessons", error);
          });
        await createIdentityAPIEndpoint(EndPoints.getTotalGeneratedCodes)
          .fetch()
          .then((res) => setTotalCodes(res.data.totalGeneratedCodes || 0))
          .catch((error) => {
            console.error("Failed to fetch total codes", error);
          });
        await createIdentityAPIEndpoint(EndPoints.getTotalActiveAccessCodes)
          .fetch()
          .then((res) => setActiveCodes(res.data.activeAccessCodes || 0))
          .catch((error) => {
            console.error("Failed to fetch active codes", error);
          });
        await createIdentityAPIEndpoint(EndPoints.getTotalInactiveAccessCodes)
          .fetch()
          .then((res) => setInactiveCodes(res.data.inactiveAccessCodes || 0))
          .catch((error) => {
            console.error("Failed to fetch inactive codes", error);
          });
      } catch (error) {
        console.error("Failed to fetch overview data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOverviewData();
  }, []);

  if (loading) {
    return <span className="loading">Loading overview...</span>;
  }

  return (
    <Box className="overview">
      <h2 className="title">Student Overview</h2>
      <Grid container className="overview-container">
        {/* Total Students */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Students
            </Typography>
            <Divider />
            <Typography variant="h4" color="success.main">
              {totalStudents}
            </Typography>
          </Paper>
        </Grid>

        {/* Active Students */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Active Students
            </Typography>
            <Divider />
            <Typography variant="h4" color="success.main">
              {activeStudents}
            </Typography>
          </Paper>
        </Grid>

        {/* Inactive Students */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Inactive Students
            </Typography>
            <Divider />
            <Typography variant="h4" color="error.main">
              {inactiveStudents}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      <h2 className="title">Lessons Overview</h2>
      <Grid container className="overview-container">
        {/* Total Lessons */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Lessons
            </Typography>
            <Divider />
            <Typography variant="h4" color="success.main">
              {totalLessons}
            </Typography>
          </Paper>
        </Grid>

        {/* Active Lessons */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Active Lessons
            </Typography>
            <Divider />
            <Typography variant="h4" color="success.main">
              {activeLessons}
            </Typography>
          </Paper>
        </Grid>

        {/* Inactive Lessons */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Inactive Lessons
            </Typography>
            <Divider />
            <Typography variant="h4" color="error.main">
              {inactiveLessons}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      <h2 className="title">Codes Overview</h2>
      <Grid container className="overview-container">
        {/* Total Codes */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Codes
            </Typography>
            <Divider />
            <Typography variant="h4" color="success.main">
              {totalCodes}
            </Typography>
          </Paper>
        </Grid>

        {/* Active Codes */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Active Codes
            </Typography>
            <Divider />
            <Typography variant="h4" color="success.main">
              {activeCodes}
            </Typography>
          </Paper>
        </Grid>

        {/* Inactive Codes */}
        <Grid item className="overview-card">
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" className="title-card">
              Total Inactive Codes
            </Typography>
            <Divider />
            <Typography variant="h4" color="error.main">
              {inactiveCodes}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Overview;
